import React, { useCallback, useEffect, useRef, useState } from "react";
import { Sparkles, X, ArrowRight } from "lucide-react";

/**
 * HomeNudge — cartão flutuante que convida o visitante a fazer a triagem
 * gratuita com a LIA. Aparece por 3 gatilhos independentes:
 *  1) 20 s de permanência na Home sem abrir a LIA
 *  2) Scroll ≥ 60 % da página (leitor engajado)
 *  3) Exit-intent: mouse deixa a viewport pela borda superior (fechando aba)
 *
 * Regras:
 * - Só na Home ("/"), só pra visitante deslogado (LiveHelpWidget cuida do
 *   filtro, então é seguro renderizar aqui).
 * - Após "Agora não", esconde pela sessão inteira (sessionStorage).
 * - Após "Sim, quero", dispara evento `raf:lia-open` (o widget abre).
 */
const SESSION_DISMISS_KEY = "raf_home_nudge_dismissed_v1";
const OPEN_DELAY_MS = 20000;   // 20s de idle → nudge
const SCROLL_THRESHOLD = 0.6;  // 60% da altura do doc → engajado

function useVisibility() {
  const [visible, setVisible] = useState(false);
  const [reason, setReason] = useState("");
  const dismissedRef = useRef(false);

  const dismissed = () => {
    try { return sessionStorage.getItem(SESSION_DISMISS_KEY) === "1"; }
    catch { return false; }
  };

  const persistDismiss = () => {
    try { sessionStorage.setItem(SESSION_DISMISS_KEY, "1"); } catch { /* */ }
    dismissedRef.current = true;
    setVisible(false);
  };

  const trigger = useCallback((why) => {
    if (dismissedRef.current || dismissed()) return;
    setReason(why);
    setVisible(true);
  }, []);

  useEffect(() => {
    if (dismissed()) return;
    // Gatilho 1 — timer de 20s
    const timerId = setTimeout(() => trigger("idle"), OPEN_DELAY_MS);
    // Gatilho 2 — scroll ≥ 60%
    const onScroll = () => {
      const doc = document.documentElement;
      const scrolled = doc.scrollTop / Math.max(1, doc.scrollHeight - doc.clientHeight);
      if (scrolled >= SCROLL_THRESHOLD) trigger("scroll");
    };
    // Gatilho 3 — exit intent (mouse saindo pelo topo)
    const onMouseLeave = (e) => {
      if (e.clientY <= 0) trigger("exit_intent");
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    document.addEventListener("mouseleave", onMouseLeave);
    return () => {
      clearTimeout(timerId);
      window.removeEventListener("scroll", onScroll);
      document.removeEventListener("mouseleave", onMouseLeave);
    };
  }, [trigger]);

  return { visible, reason, persistDismiss };
}

export default function HomeNudge() {
  const { visible, reason, persistDismiss } = useVisibility();
  const [entering, setEntering] = useState(false);

  useEffect(() => {
    if (visible) {
      const t = setTimeout(() => setEntering(true), 30);
      return () => clearTimeout(t);
    }
    setEntering(false);
  }, [visible]);

  if (!visible) return null;

  const messageByReason = {
    idle: {
      title: "Precisa de uma direção agora?",
      body: "Fazemos uma triagem gratuita em 90 segundos. Sem cadastro, sem custo.",
    },
    scroll: {
      title: "Continua com dúvidas?",
      body: "A LIA responde suas perguntas e sugere o melhor caminho — de graça, em 90s.",
    },
    exit_intent: {
      title: "Antes de sair — posso te ajudar?",
      body: "90 segundos com a LIA valem muito. Triagem gratuita, sem compromisso.",
    },
  }[reason] || messageByReason?.idle;

  const openLia = () => {
    try { sessionStorage.setItem(SESSION_DISMISS_KEY, "1"); } catch { /* */ }
    window.dispatchEvent(new CustomEvent("raf:lia-open", { detail: { source: `nudge_${reason}` } }));
    persistDismiss();
  };

  return (
    <div
      data-testid={`home-nudge-${reason}`}
      className={`fixed z-40 bottom-[92px] right-4 sm:right-6 max-w-[320px] transition-all duration-500 ${
        entering ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
      }`}
      role="dialog"
      aria-label="Convite para triagem gratuita"
    >
      <div className="relative bg-white rounded-2xl shadow-2xl border border-emerald-200 p-4 pr-6">
        <button
          onClick={persistDismiss}
          aria-label="Fechar convite"
          data-testid="home-nudge-close"
          className="absolute top-2 right-2 w-6 h-6 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-500 flex items-center justify-center transition"
        >
          <X className="w-3.5 h-3.5" />
        </button>
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 text-white flex items-center justify-center shrink-0 shadow">
            <Sparkles className="w-5 h-5" />
          </div>
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-bold">LIA · Grátis · 90s</div>
            <div className="font-display font-bold text-[#0B2B62] mt-0.5 leading-tight">{messageByReason.title}</div>
            <div className="text-xs text-slate-600 mt-1 leading-snug">{messageByReason.body}</div>
          </div>
        </div>
        <div className="flex items-center gap-2 mt-3">
          <button
            onClick={openLia}
            data-testid="home-nudge-accept"
            className="flex-1 inline-flex items-center justify-center gap-1 px-3 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold shadow transition"
          >
            Sim, quero começar <ArrowRight className="w-4 h-4" />
          </button>
          <button
            onClick={persistDismiss}
            data-testid="home-nudge-dismiss"
            className="text-xs text-slate-500 hover:text-slate-700 px-2 py-2"
          >
            Agora não
          </button>
        </div>
      </div>
    </div>
  );
}
